/**
 * URL PHOTO IMPORT UTILITY
 * 
 * Handles importing photos from external URLs (from CSV imports)
 * - Downloads images from URLs
 * - Compresses them
 * - Uploads to Firebase Storage
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { storage, db } from '../firebase/config';

interface UrlImportResult {
  polishId: string;
  originalUrl: string;
  newUrl?: string;
  status: 'success' | 'failed' | 'skipped';
  error?: string;
}

interface PolishWithUrl {
  id: string;
  externalPhotoUrl: string;
  brand: string;
  name: string;
}

/**
 * Compress an image blob
 */
async function compressImageBlob(
  blob: Blob, 
  maxWidth: number = 1200, 
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      let { width, height } = img;
      
      // Only resize if larger than maxWidth
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (resultBlob) => {
          if (resultBlob) {
            resolve(resultBlob);
          } else {
            reject(new Error('Compression failed'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(blob);
  });
}

/**
 * Download image from URL
 */
async function downloadImage(url: string): Promise<Blob> {
  // Try direct fetch first
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (!contentType?.startsWith('image/')) {
      throw new Error('Not an image');
    }
    
    return await response.blob();
  } catch (error) {
    // If CORS fails, try through a proxy (user would need to set this up)
    throw new Error(`Failed to download: ${error}`);
  }
}

/**
 * Upload image blob to Firebase Storage
 */
async function uploadToFirebase(
  userId: string,
  polishId: string,
  blob: Blob,
  filename: string
): Promise<string> {
  const storageRef = ref(storage, `users/${userId}/polishes/${polishId}/${filename}`);
  const snapshot = await uploadBytes(storageRef, blob, {
    contentType: 'image/jpeg',
    cacheControl: 'public, max-age=31536000, immutable',
  });
  return getDownloadURL(snapshot.ref);
}

/**
 * Update polish document with new photo
 */
async function updatePolishWithPhoto(
  userId: string,
  polishId: string,
  photoUrl: string
): Promise<void> {
  const polishRef = doc(db, 'users', userId, 'items', polishId);
  
  const photoData = {
    url: photoUrl,
    uploadedAt: new Date().toISOString(),
    source: 'url_import'
  };
  
  await updateDoc(polishRef, {
    photos: arrayUnion(photoData)
  });
}

/**
 * Import a single photo from URL
 */
export async function importPhotoFromUrl(
  userId: string,
  polishId: string,
  url: string,
  onProgress?: (status: string) => void
): Promise<UrlImportResult> {
  try {
    // Validate URL
    if (!url || !url.startsWith('http')) {
      return {
        polishId,
        originalUrl: url,
        status: 'skipped',
        error: 'Invalid URL'
      };
    }
    
    onProgress?.('Downloading...');
    
    // Download image
    const blob = await downloadImage(url);
    
    onProgress?.('Compressing...');
    
    // Compress
    const compressed = await compressImageBlob(blob);
    
    onProgress?.('Uploading...');
    
    // Generate filename
    const timestamp = Date.now();
    const filename = `imported_${timestamp}.jpg`;
    
    // Upload to Firebase
    const newUrl = await uploadToFirebase(userId, polishId, compressed, filename);
    
    onProgress?.('Saving...');
    
    // Update polish document
    await updatePolishWithPhoto(userId, polishId, newUrl);
    
    return {
      polishId,
      originalUrl: url,
      newUrl,
      status: 'success'
    };
  } catch (error) {
    return {
      polishId,
      originalUrl: url,
      status: 'failed',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Batch import photos from URLs
 */
export async function batchImportPhotosFromUrls(
  userId: string,
  polishes: PolishWithUrl[],
  onProgress?: (current: number, total: number, status: string) => void
): Promise<UrlImportResult[]> {
  const results: UrlImportResult[] = [];
  
  for (let i = 0; i < polishes.length; i++) {
    const polish = polishes[i];
    
    onProgress?.(i + 1, polishes.length, `Processing ${polish.brand} - ${polish.name}`);
    
    const result = await importPhotoFromUrl(
      userId,
      polish.id,
      polish.externalPhotoUrl
    );
    
    results.push(result);
    
    // Small delay to avoid rate limiting
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  return results;
}

/**
 * Check if a URL is likely accessible (quick HEAD request)
 */
export async function checkUrlAccessible(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'omit'
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get polishes that have external URLs but no uploaded photos
 */
export function getPolishesWithPendingUrls(polishes: any[]): PolishWithUrl[] {
  return polishes.filter(p => 
    p.externalPhotoUrl && 
    (!p.photos || p.photos.length === 0)
  ).map(p => ({
    id: p.id,
    externalPhotoUrl: p.externalPhotoUrl,
    brand: p.brand,
    name: p.name
  }));
}
